import React, { useCallback, useEffect, useState } from 'react';
import {
    type ArticleValidationProcessStatus,
    CATEGORY_LEGAL_ANNOUCEMENT,
    type DossierArticle,
    type DossierArticleWithInvoice,
    DossierRoutingContext,
    REFRESH_CURRENT_DOSSIER_CTX,
    RESET_DOS_DOCS_CTX,
    type UpdateArticleParamsType,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierArticle,
    useDossierDocumentsCtxDispatch,
    useDossierLegalAnnoucements,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    VALIDATOR_TYPE_LEGAL_ANNOUCEMENT,
} from '@europrocurement/l2d-modules';
import { Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useModal } from '@europrocurement/flexy-components';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { PrestationArticlesApiApiArticlesPrestationIdAttacherAnnonceLegalePatchRequest } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import { useNavigate } from 'react-router';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierLegalAnnoucementsFooter from '../DossierLegalAnnoucementsFooter/DossierLegalAnnoucementsFooter';
import AddLegalAnnoucementModal from '../AddLegalAnnoucementModal/AddLegalAnnoucementModal';
import { AddLegalAnnoucementFormData } from '../AddLegalAnnoucementForm/AddLegalAnnoucementForm';
import { DossierProcessesArticlesProps } from '../DossierProcesses/DossierProcessesType';
import EditLegalAnnoucementForm, {
    EditLegalAnnoucementFormFormData,
} from '../EditLegalAnnoucementForm/EditLegalAnnoucementForm';
import SelectLegalAnnoucementForm from '../SelectLegalAnnoucementForm/SelectLegalAnnoucementForm';
import DossierArticlesProcessesList from '../DossierArticlesProcessesList/DossierArticlesProcessesList';
import DossierProcessesBulkActionsBar from '../DossierProcessesBulkActionsBar/DossierProcessesBulkActionsBar';
import useDossierBulkActions from '../../hooks/useDossierBulkActions';

const DossierLegalAnnoucements: React.FunctionComponent<DossierProcessesArticlesProps> = function ({
    articles,
}) {
    const validatorTypeId = VALIDATOR_TYPE_LEGAL_ANNOUCEMENT;
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();
    const dossierDocumentsdispatch = useDossierDocumentsCtxDispatch();
    const navigate = useNavigate();
    const { getDossierRoute } = React.useContext(DossierRoutingContext);

    const refreshDossier = useCallback(
        (dossierId?: number) => {
            if (dispatch) {
                dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
            }
            if (dossierDocumentsdispatch) {
                dossierDocumentsdispatch({ type: RESET_DOS_DOCS_CTX });
            }
            if (dossierId) {
                const route = getDossierRoute(dossierId as number);
                if (route) {
                    navigate(route);
                }
            }
        },
        [dispatch, dossierDocumentsdispatch, getDossierRoute, navigate],
    );

    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [processing, setProcessing] = useState<boolean>(false);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [legalAnnoucements, setLegalAnnoucements] = useState<Array<LegalAnnoucement>>([]);
    const [legalAnnoucementsLoaded, setLegalAnnoucementsLoaded] = useState<boolean>(true);
    const { prestationArticlesApi } = useFormaliteService();
    const {
        getProcessesByArticle,
        createValidatorProcess,
        canCreateValidatorProcess,
        findProcessByArticle,
    } = useDossierProcesses();

    const {
        handleDeleteArticle,
        handlePublishLegalAnnoucement,
        loadLegalAnnoucements,
        actionShowLegalAnouncement,
        actionRemoveLinkedLegalAnouncement,
        actionDeleteArticlePresta,
        actionAttachLegalAnouncement,
        actionPublishLegalAnouncement,
    } = useDossierProcessesActionsAndRender();

    const { addLegalAnnoucement, deleteLegalAnnoucement, getLegalAnnoucementTypes } =
        useDossierLegalAnnoucements();
    const { updateArticle } = useDossierArticle();
    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();

    const handleAttachLegalAnnoucement = async (idLegalAnnoucement: number, articleId: number) => {
        if (!dossier?.id) {
            return;
        }

        setProcessing(true);

        try {
            const process = findProcessByArticle(dossier, [validatorTypeId], articleId);
            if (!process) {
                throw new Error("Aucun processus de validation en cours sur l'article");
            }

            const request: PrestationArticlesApiApiArticlesPrestationIdAttacherAnnonceLegalePatchRequest =
                {
                    id: articleId,
                    apiArticlesPrestationIdAttacherAnnonceLegalePatchRequest: {
                        idAnnonceLegale: idLegalAnnoucement,
                    },
                };

            const response =
                await prestationArticlesApi.apiArticlesPrestationIdAttacherAnnonceLegalePatch(
                    request,
                );

            if (response.status === 200) {
                modalActions.close();
                enqueueSnackbar('Votre annonce a bien été rattachée');

                // Le processus de validation a été mis à jour, récupération du statut lié au valideur
                refreshDossier();

                setLegalAnnoucementsLoaded(false);
            } else {
                enqueueSnackbar(
                    'Une erreur est survenue durant le rattachement de votre annonce légale',
                    { variant: 'error' },
                );
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                'Une erreur est survenue durant le rattachement de votre annonce légale',
                { variant: 'error' },
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleCloseModal = () => {
        modalActions.close();
    };

    const handleDeleteLegalAnnoucement = async (article: DossierArticle) => {
        if (!dossier) {
            return;
        }
        const { status } = await deleteLegalAnnoucement(
            article,
            dossier,
            validatorTypeId,
            'Le rattachement a bien été supprimé',
            'Une erreur est survenue durant la suppression du rattachement',
            'to-link',
        );

        if (status === 200) {
            // Peut être une possibilité plus fine de mettre a jour les données du valideur et de son statut sans avoir a refresh
            refreshDossier();
            setLegalAnnoucementsLoaded(false);
        }

        handleCloseModal();
    };

    const handleAddLegalAnnoucement = async (data: AddLegalAnnoucementFormData) => {
        if (!dossier) {
            return;
        }
        setProcessing(true);

        try {
            const articleId = await addLegalAnnoucement(
                data.title,
                data.prestationId as number,
                data.typeAnnonce as number,
            );
            await createValidatorProcess(dossier.id as number, [articleId]);
            enqueueSnackbar('Votre annonce a bien été ajoutée');
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'ajout de votre annonce",
            );
        } finally {
            setProcessing(false);
            setShowAddModal(false);
        }
    };

    const onSubmitEdiLegalAnnoucement = async (data: EditLegalAnnoucementFormFormData) => {
        if (!dossier) {
            return;
        }
        setProcessing(true);
        try {
            await updateArticle({
                prestaArticleId: data.prestaArticleId,
                prestationId: data.prestationId,
                label: data.title,
                factArticleId: data.articleId,
                typeArticleId: data.typeAnnonce,
            } as UpdateArticleParamsType);
            modalActions.close();
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'édition de la démarche",
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleEditLegalAnnoucement = useCallback(
        async (prestaArticle: DossierArticle) => {
            if (!dossier) {
                return;
            }

            const results = await getLegalAnnoucementTypes();
            const legalAnnoucementTypes = results || [];
            const currentType = results.find((legal) => legal.id === prestaArticle.idStype);

            modalActions.call(
                <EditLegalAnnoucementForm
                    onCancel={() => {
                        setProcessing(false);
                        modalActions.close();
                    }}
                    prestaArticle={prestaArticle}
                    onSubmit={onSubmitEdiLegalAnnoucement}
                    dossier={dossier}
                    legalAnnoucementTypes={legalAnnoucementTypes}
                    currentType={currentType}
                />,
            );
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dossier, modalActions, processing],
    );

    const { canDissociate, canMove, handleDissociate, handleMove } = useDossierBulkActions({
        selectedIds,
        refreshDossier,
    });

    const getItemActions = useCallback(
        (item: DossierArticleWithInvoice) => {
            if (!dossier) {
                return [];
            }
            let actions: Array<DossierArticleProcessMenuItemProps> = [];
            let status: ArticleValidationProcessStatus = 'to-link';
            const processes = getProcessesByArticle(dossier, validatorTypeId, item.id);

            if (processes.length > 0) {
                status = processes[0].statusCode as ArticleValidationProcessStatus;
            }

            const showAnnoucementAction = actionShowLegalAnouncement(
                item.id,
                dossier,
                validatorTypeId,
                legalAnnoucements,
            );

            if (
                ['to-link'].includes(status) &&
                canCreateValidatorProcess(dossier, validatorTypeId)
            ) {
                actions = [
                    ...actions,
                    actionAttachLegalAnouncement(() => {
                        modalActions.call(
                            <SelectLegalAnnoucementForm
                                onItemSelected={handleAttachLegalAnnoucement}
                                article={item}
                                sTypeId={item.idStype}
                                title="Rechercher une annonce légale"
                                placeholder="Rechercher une annonce légale"
                            />,
                        );
                    }),
                    {
                        label: 'Modifier la démarche',
                        icon: 'edit' as IconName,
                        onClick: () => handleEditLegalAnnoucement(item),
                    },
                    ...(canMove
                        ? [
                              {
                                  label: 'Déplacer',
                                  icon: 'file-import' as IconName,
                                  onClick: () => handleMove([item.id]),
                              },
                          ]
                        : []),
                    ...(canDissociate
                        ? [
                              {
                                  label: 'Dissocier',
                                  icon: 'folders' as IconName,
                                  onClick: () => handleDissociate([item.id]),
                              },
                          ]
                        : []),
                    actionDeleteArticlePresta(
                        () =>
                            handleDeleteArticle(item.id.toString(), setProcessing, refreshDossier),
                        () => modalActions.close(),
                    ),
                ];
            } else if (['ongoing', 'default', 'rescheduled'].includes(status)) {
                actions = [
                    ...actions,
                    showAnnoucementAction,
                    actionPublishLegalAnouncement(
                        () =>
                            handlePublishLegalAnnoucement(
                                item,
                                dossier,
                                validatorTypeId,
                                () => {
                                    setLegalAnnoucementsLoaded(false);
                                    refreshDossier();
                                },
                                () => handleCloseModal(),
                            ),
                        () => handleCloseModal,
                    ),
                    // The deletion of legalAnoucement is only available if not billed
                    ...(item.hasInvoices === false
                        ? [
                              actionRemoveLinkedLegalAnouncement(
                                  handleDeleteLegalAnnoucement,
                                  handleCloseModal,
                              ),
                          ]
                        : []),
                ];
            } else if (['validate'].includes(status)) {
                actions = [
                    ...actions,
                    showAnnoucementAction,
                    // The deletion of legalAnoucement is only available if not billed
                    ...(item.hasInvoices === false
                        ? [
                              actionRemoveLinkedLegalAnouncement(
                                  handleDeleteLegalAnnoucement,
                                  handleCloseModal,
                              ),
                          ]
                        : []),
                ];
            } else if (['failed'].includes(status)) {
                actionDeleteArticlePresta(
                    () => handleDeleteArticle(item.id.toString(), setProcessing, refreshDossier),
                    () => modalActions.close(),
                );
            }

            return actions;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            canCreateValidatorProcess,
            dossier,
            getProcessesByArticle,
            legalAnnoucements,
            validatorTypeId,
        ],
    );

    useEffect(() => {
        if (!dossier?.id) {
            return;
        }

        if (!legalAnnoucementsLoaded) {
            setLegalAnnoucementsLoaded(true);
            loadLegalAnnoucements(dossier, CATEGORY_LEGAL_ANNOUCEMENT, [validatorTypeId])
                .then((items) => {
                    setLegalAnnoucements(items);
                })
                .catch(() => {
                    setLegalAnnoucements([]);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [legalAnnoucementsLoaded]);

    useEffect(() => {
        if (!dossier?.id) {
            return;
        }
        setLegalAnnoucementsLoaded(false);
    }, [dossier?.id]);

    if (!dossier) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    }
    return (
        <>
            <Box sx={{ gap: '16px', display: 'grid' }}>
                <DossierProcessesBulkActionsBar
                    selectedIds={selectedIds}
                    onClearSelection={() => setSelectedIds([])}
                    canDissociate={canDissociate}
                    onDissociate={handleDissociate}
                    canMove={canMove}
                    onMove={handleMove}
                />
                <DossierArticlesProcessesList
                    articles={articles}
                    dossier={dossier}
                    validatorTypeId={validatorTypeId}
                    legalAnnoucements={legalAnnoucements}
                    getItemActions={getItemActions}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                />
                <DossierLegalAnnoucementsFooter
                    canAddLegalAnnoucement={canCreateValidatorProcess(dossier, validatorTypeId)}
                    handleAddLegalAnnoucement={() => setShowAddModal(true)}
                />
            </Box>
            <AddLegalAnnoucementModal
                showModal={showAddModal}
                processing={processing}
                onClose={() => setShowAddModal(false)}
                onSubmit={handleAddLegalAnnoucement}
            />
        </>
    );
};

export default DossierLegalAnnoucements;
